import React, { useState, useRef } from "react";
import styled from "styled-components";
import { addDays, subDays, format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths, isSameDay, isSameMonth, differenceInWeeks, startOfDay } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { FaRegCalendarAlt } from "react-icons/fa";


function ActionBar({
    startDate,
    setStartDate,
    goToToday,
    onMemberSelect,
    selectedMembers,
    currentView,
    setCurrentView,
    daysToShow,
    weekNum,
    setWeekNum
}) {
    const { groupMembers, groupInfo } = useAuth();
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const calendarRef = useRef(null);

    // Calculate maximum allowed week number based on course end date
    const getMaxWeekNum = () => {
        if (!groupInfo || !groupInfo.startDate || !groupInfo.endDate) {
            return null;
        }
        const courseStartWeek = startOfWeek(groupInfo.startDate, { weekStartsOn: 1 });
        const courseEndWeek = startOfWeek(groupInfo.endDate, { weekStartsOn: 1 });
        const totalWeeks = differenceInWeeks(courseEndWeek, courseStartWeek);
        return Math.floor(totalWeeks / 5) * 5;
    };

    const iconButtons = [
        { icon: "/images/day.svg", view: 'Day' },
        { icon: "/images/week.svg", view: 'Week' },
    ];

    function handlePrevDate() {
        if (currentView === 'Day') {
            let newStartDate = subDays(startDate, daysToShow);

            if (groupInfo?.startDate) {
                const courseStartDate = startOfDay(new Date(groupInfo.startDate));
                if (startDate.getTime() === courseStartDate.getTime()) return;
                if (newStartDate < courseStartDate) newStartDate = courseStartDate;
            }

            setStartDate(newStartDate);
        } else if (currentView === 'Week' && weekNum > 0) {
            setWeekNum(prevWeekNum => Math.max(prevWeekNum - 5, 0));
        }
    }

    function handleNextDate() {
        if (currentView === 'Day') {
            const newStartDate = addDays(startDate, daysToShow);

            // Check if the new start date would go beyond the course end date
            if (groupInfo && groupInfo.endDate) {
                const courseEndDate = startOfDay(new Date(groupInfo.endDate));
                if (newStartDate > courseEndDate) {
                    return;
                }
            }

            setStartDate(newStartDate);
        } else if (currentView === 'Week') {
            const maxWeek = getMaxWeekNum();
            if (maxWeek === null) {
                setWeekNum(prevWeekNum => prevWeekNum + 5);
            } else {
                setWeekNum(prevWeekNum => Math.min(prevWeekNum + 5, maxWeek));
            }
        }
    }

    function handleDateSelect(selectedDate) {
        setStartDate(selectedDate);
        setShowCalendar(false);
    }

    function generateCalendarDays() {
        const monthStart = startOfMonth(calendarDate);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);

        const days = [];
        let day = startDate;

        while (day <= endDate) {
            days.push(day);
            day = addDays(day, 1);
        }

        return days;
    }

    const calendarDays = generateCalendarDays();

    return (
        <ActionBarWrapper>
            <SwitchDateGroup>
                <SwitchDateButton onClick={handlePrevDate}>
                    <img loading="lazy" src="/images/left_arrow.svg" alt="" />
                </SwitchDateButton>
                <SwitchDateButton onClick={goToToday}>
                    <Txtwarp>Today</Txtwarp>
                </SwitchDateButton>
                {/* Calendar Picker Button */}
                <CalendarContainer ref={calendarRef}>
                    <SwitchDateButton onClick={() => setShowCalendar(!showCalendar)}>
                            <FaRegCalendarAlt size={16} color="#4b5563" />
                    </SwitchDateButton>

                    {showCalendar && (
                        <CalendarDropdown>
                            <CalendarHeader>
                                <CalendarNavButton onClick={() => setCalendarDate(subMonths(calendarDate, 1))}>
                                    &lt;
                                </CalendarNavButton>
                                <CalendarMonth>
                                    {format(calendarDate, 'MMMM yyyy')}
                                </CalendarMonth>
                                <CalendarNavButton onClick={() => setCalendarDate(addMonths(calendarDate, 1))}>
                                    &gt;
                                </CalendarNavButton>
                            </CalendarHeader>

                            <CalendarGrid>
                                <CalendarWeekdays>
                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                        <CalendarWeekday key={day}>{day}</CalendarWeekday>
                                    ))}
                                </CalendarWeekdays>

                                <CalendarDays>
                                    {calendarDays.map((day, index) => (
                                        <CalendarDay
                                            key={index}
                                            $isCurrentMonth={isSameMonth(day, calendarDate)}
                                            $isSelected={isSameDay(day, startDate)}
                                            $isToday={isSameDay(day, new Date())}
                                            onClick={() => handleDateSelect(day)}
                                        >
                                            {format(day, 'd')}
                                        </CalendarDay>
                                    ))}
                                </CalendarDays>
                            </CalendarGrid>
                        </CalendarDropdown>
                    )}
                </CalendarContainer>
                <SwitchDateButton onClick={handleNextDate}>
                    <img loading="lazy" src="/images/right_arrow.svg" alt="" />
                </SwitchDateButton>
            </SwitchDateGroup>

            <IconButtonGroup>
                {iconButtons.map((button, index) => (
                    <BoxWrapper key={index} onClick={() => setCurrentView(button.view)}>
                        <BackgroundColor $isActive={currentView === button.view}>
                            <img loading="lazy" src={button.icon} alt={button.view} />
                        </BackgroundColor>
                    </BoxWrapper>
                ))}
            </IconButtonGroup>

            <MemberInitialGroup>
                {groupMembers.map((member) => {
                    const isActive = selectedMembers.some(m => m.email === member.email);
                    return (
                        <AvatarButton
                            key={member.email}
                            onClick={() => onMemberSelect(member)}
                            $active={isActive}
                            $color={member.color}
                            aria-pressed={isActive}
                            title={member.name}
                            data-tooltip={member.name}
                        >
                            {member.initial}
                        </AvatarButton>
                    );
                })}
            </MemberInitialGroup>
        </ActionBarWrapper>
    );
}

/* === Styled Components === */

const BoxWrapper = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #969a97;
    & + & { border-left: 0; }
    margin-top: 4px;
    width: 40px;
    height: 40px;
    background: none;
    font: inherit;
    cursor: pointer;
    overflow: hidden;
    &:first-child {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
    }
    &:last-child {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
    }
    @media (max-width: 991px) {
        white-space: initial;
    }
`;

const Txtwarp = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: #4b5563;
`;

const ActionBarWrapper = styled.div`
    display: flex;
    margin-top: 1px;
    gap: 10px;
    padding-top: 4px;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-y: contain;
    touch-action: pan-x;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const SwitchDateGroup = styled.div`
    display: flex;
    margin-left: auto;
    flex-shrink: 0;

    @media (max-width: 600px) {
        margin-left: 0;
    }
`;

const IconButtonGroup = styled.div`
    display: flex;
    gap: 0;
    flex-shrink: 0;
    img { width: 27px; height: 27px; object-fit: contain; }
`;

const MemberInitialGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 20px;
    font-family: Inter, sans-serif;
    flex-shrink: 0;
    @media (max-width: 991px) {
        white-space: initial;
    }
`;

const BackgroundColor = styled.div`
    background-color: ${props => props.$isActive ? "#9333ea" : "#fff"};
    padding: 0 10px 2px;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SwitchDateButton = styled(BoxWrapper)`
    &:active {
        background-color: #e0e0e0;
    }
    transition: background-color 0.1s;

    ${Txtwarp} {
        padding: 0 12px;
        width: auto;
    }
    min-width: 40px;
    width: auto;
    padding: 0;
`;

const AvatarButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 1.2rem;
  cursor: pointer;
  user-select: none;
  position: relative;
  color: ${({ $active }) => ($active ? '#fff' : '#374151')};
  background: ${({ $active, $color }) => ($active ? '#9333ea' : $color)};
  border: 1px solid ${({ $active }) => ($active ? '#9333ea' : '#f8fafc')};

  &:hover { filter: brightness(0.98); }

  @media (hover: hover) and (pointer: fine) {
    &[data-tooltip]::after {
      content: attr(data-tooltip);
      position: absolute;
      top: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%);
      background: #111827;
      color: #fff;
      font: 600 12px/1.2 Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
      padding: 6px 8px;
      border-radius: 6px;
      white-space: nowrap;
      box-shadow: 0 6px 18px rgba(0,0,0,.15);
      pointer-events: none;
      opacity: 0;
      visibility: hidden;
      transition: opacity .12s ease, transform .12s ease;
      z-index: 30;
    }
    &[data-tooltip]::before {
      content: "";
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translate(-50%, 0);
      border: 6px solid transparent;
      border-bottom-color: #111827;
      opacity: 0;
      visibility: hidden;
      transition: opacity .12s ease;
      z-index: 29;
    }
    &[data-tooltip]:hover::after,
    &[data-tooltip]:hover::before {
      opacity: 1;
      visibility: visible;
    }
    &[data-tooltip]:hover::after {
      transform: translate(-50%, 2px);
    }
  }

  &:focus-visible {
    outline: 2px solid #9333ea;
    outline-offset: 2px;
  }
`;

const CalendarContainer = styled.div`
    position: relative;
    button {
        border-left: 0;              /* prevent double line on left */
        border-right: 0;
        border-radius: 0 !important;
    }
`;

const CalendarDropdown = styled.div`
    position: absolute;
    top: 45px;
    right: 0;
    background: white;
    border: 1px solid #ccc;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    padding: 16px;
    min-width: 280px;
`;

const CalendarHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const CalendarNavButton = styled.button`
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    
    &:hover {
        background-color: #f0f0f0;
    }
`;

const CalendarMonth = styled.div`
    font-weight: bold;
    font-size: 16px;
`;

const CalendarGrid = styled.div`
    display: flex;
    flex-direction: column;
`;

const CalendarWeekdays = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    margin-bottom: 8px;
`;

const CalendarWeekday = styled.div`
    text-align: center;
    font-size: 12px;
    font-weight: bold;
    color: #666;
    padding: 4px;
`;

const CalendarDays = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
`;

const CalendarDay = styled.button`
    padding: 8px;
    border: none;
    background: none;
    cursor: pointer;
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.2s;
    
    color: ${props => props.$isCurrentMonth ? '#333' : '#ccc'};
    background-color: ${props => {
        if (props.$isSelected) return '#9333ea';
        if (props.$isToday) return '#e3f2fd';
        return 'transparent';
    }};
    color: ${props => props.$isSelected ? 'white' : (props.$isCurrentMonth ? '#333' : '#ccc')};
    
    &:hover {
        background-color: ${props => props.$isSelected ? '#ad7ed8ff' : '#f0f0f0'};
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

export default ActionBar;
